import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { H4, NamedLink } from '../../components';
import css from './ProfilePage.module.css';

const SectionFarmerProfile = props => {
  const { publicData, intl } = props;
  const {
    farmName,
    address,
    street,
    city,
    postalCode,
    province,
    operationArea,
    cultivationType,
    organicCertificate,
    certificateInfo,
    pickupDays,
    pickupStartTime,
    pickupEndTime,
    sameDayPickup,
    allowFarmVisits,
    miniSlogan,
    websiteUrl,
    instagramUrl,
    facebookUrl,
  } = publicData || {};

  const hasFarmInfo = farmName || address || street || city || province || operationArea || cultivationType || pickupDays;
  const hasOrganicCertificate = organicCertificate === 'yes';

  if (!hasFarmInfo) {
    return null;
  }

  const formatPickupDays = (days) => {
    if (!days || days.length === 0) return null;
    return days.map(day => intl.formatMessage({ id: `ProfilePage.farmerProfile.${day}` })).join(', ');
  };

  return (
    <div className={css.farmerProfileSection}>
      <div className={css.headerWithBadge}>
        <H4 as="h2" className={css.sectionTitle}>
          <FormattedMessage id="ProfilePage.farmerProfile.title" />
        </H4>
        {hasOrganicCertificate && (
          <div className={css.organicBadge}>
            <span className={css.badgeIcon}>🌿</span>
            <FormattedMessage id="ProfilePage.farmerProfile.certifiedOrganic" />
          </div>
        )}
      </div>

      {miniSlogan && (
        <div className={css.miniSlogan}>
          <em>"{miniSlogan}"</em>
        </div>
      )}
      
      <div className={css.farmerInfo}>
        {farmName && (
          <div className={css.infoRow}>
            <span className={css.infoLabel}>
              <FormattedMessage id="ProfilePage.farmerProfile.farmName" />:
            </span>
            <span className={css.infoValue}>{farmName}</span>
          </div>
        )}

        {(address || street || city || postalCode || province) && (
          <div className={css.infoRow}>
            <span className={css.infoLabel}>
              <FormattedMessage id="ProfilePage.farmerProfile.location" />:
            </span>
            <span className={css.infoValue}>
              {[
                street,
                city,
                province,
                postalCode,
                address?.address || address
              ].filter(Boolean).join(', ')}
            </span>
          </div>
        )}

        {operationArea && (
          <div className={css.infoRow}>
            <span className={css.infoLabel}>
              <FormattedMessage id="ProfilePage.farmerProfile.operationArea" />:
            </span>
            <span className={css.infoValue}>
              {operationArea.address || operationArea}
            </span>
          </div>
        )}

        {cultivationType && (
          <div className={css.infoRow}>
            <span className={css.infoLabel}>
              <FormattedMessage id="ProfilePage.farmerProfile.cultivationType" />:
            </span>
            <span className={css.infoValue}>
              <FormattedMessage id={`ProfilePage.farmerProfile.cultivationType.${cultivationType}`} />
            </span>
          </div>
        )}

        {pickupDays && pickupDays.length > 0 && (
          <div className={css.infoRow}>
            <span className={css.infoLabel}>
              <FormattedMessage id="ProfilePage.farmerProfile.pickupDays" />:
            </span>
            <span className={css.infoValue}>
              {formatPickupDays(pickupDays)}
              {pickupStartTime && pickupEndTime && ` (${pickupStartTime} - ${pickupEndTime})`}
              {sameDayPickup && (
                <span className={css.sameDayBadge}>
                  {' '}• <FormattedMessage id="ProfilePage.farmerProfile.sameDayAvailable" />
                </span>
              )}
            </span>
          </div>
        )}

        {allowFarmVisits && (
          <div className={css.infoRow}>
            <span className={css.infoLabel}>
              <FormattedMessage id="ProfilePage.farmerProfile.farmVisits" />:
            </span>
            <span className={css.infoValue}>
              <FormattedMessage id={`ProfilePage.farmerProfile.farmVisits.${allowFarmVisits}`} />
            </span>
          </div>
        )}

        {(websiteUrl || instagramUrl || facebookUrl) && (
          <div className={css.infoRow}>
            <span className={css.infoLabel}>
              <FormattedMessage id="ProfilePage.farmerProfile.socialLinks" />:
            </span>
            <span className={css.infoValue}>
              {websiteUrl && (
                <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className={css.socialLink}>
                  🌐 Website
                </a>
              )}
              {instagramUrl && (
                <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className={css.socialLink}>
                  📷 Instagram
                </a>
              )}
              {facebookUrl && (
                <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className={css.socialLink}>
                  📘 Facebook
                </a>
              )}
            </span>
          </div>
        )}
      </div>

      <div className={css.farmerActions}>
        <NamedLink className={css.actionButton} name="SearchPage" to={{ search:"address=Espa%C3%B1a&bounds=28.08136788%2C-15.4942265%2C27.93951364%2C-15.62583784&mapSearch=true" }}>
          <FormattedMessage id="ProfilePage.farmerProfile.viewProducts" />
        </NamedLink>
      </div>
    </div>
  );
};

export default SectionFarmerProfile;
